import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Typography, Grid, Box, InputLabel, InputAdornment, Input, Button, useMediaQuery } from '@mui/material';
import { getText, isEqual, getRoundedFromSum, getRoundedDirSum  } from '../../libs/utils';
import PSIcon from '../../components/PSIcon';
import { ArrowRight, Warning, CloseIcon } from '../../assets/images/icons';
import { styled, useTheme } from '@mui/material/styles';
import Notice from '../main/Notice';
import REST from '../../libs/API';
import { calc, calcDelta, toFineFixed } from '../../libs/calc';
import { useNavigate } from "react-router-dom";

const MyInput = styled(Input)(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.primary.main,
  padding: '13px 9px 13px 18px !important'
}))

const MyInputAdornment = styled(InputAdornment)(({ theme }) => ({
  ".MuiTypography-root": {
    fontSize: '16px',
    color: theme.palette.primary.grey,
    fontWeight: 400
  }
}))

const RateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  ".item": {
    display: 'flex',
    alignItems: 'center'
  },
  ".arrow": {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 20px'
  },
  [theme.breakpoints.only('xs')]: {
    margin: '0 0 20px'
  }
}))

const UpdateRateBox = styled(Box)(({ theme }) => ({
  width: '300px',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center',
  boxShadow: '0px -2px 14px -4px rgba(0, 0, 0, 0.12), 0px 8px 14px -4px rgba(0, 0, 0, 0.16)',
  borderRadius:'20px',
  padding: '24px 20px'
}))

const ArrowRightBox = styled(Box)(({theme}) => ({
  margin: '58px 0 0',
  [theme.breakpoints.only('xs')]: {
    margin: '10px 0',
    transform: 'rotate(90deg)'
  }
}))

const RepeatOrder = ({ order, open, closeDialog }) => {
  let hashTimer;
  const theme = useTheme();
  let isXS = useMediaQuery(theme.breakpoints.only('xs'));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updateRate, setUpdateRate] = useState(false);
  // const [orderDetails, setOrderDetails] = useState({});
  const [currentRate, setCurrentRate] = useState({});
  const [amounts, setAmounts] = useState({ fromSum: '', toSum: '' });

  const getRepeatInfo = (withNewRate = false) => {
    withNewRate && setLoading(true);

    REST.repeatInfo(order.code).then(result => {
      if (result.success) {
        const { data } = result;
        setCurrentRate({
          lastHash: data.rate.hash,
          rate: data.rate.rate,
          rateValues: data.rate.rateValues,
          precisions: {
            from: data.rate.fromPrecision,
            to: data.rate.toPrecision
          },
          fromMultiple: data.rate.fromMultiple || 0,
          toMultiple: data.rate.toMultiple || 0,
          operation: data.rate.operation,
          limits: data.rate.amountInputs,
          to: data.to,
          from: data.from
        });
        setAmounts({
          fromSum: withNewRate ? amounts.fromSum : data.from.amount,
          toSum: withNewRate ? amounts.toSum : data.to.amount,
        });
        clearTimeout(hashTimer);
        hashTimer = setTimeout(() => setUpdateRate(true), 1000 * data.rate.hashLifetime);
        setLoading(false);
        withNewRate && setUpdateRate(false);
      }
    })
  }

  useEffect(() => {
    getRepeatInfo();

    return () => clearTimeout(hashTimer);
  }, []);

  useEffect(() => {
    currentRate && handleCalc({
      target: {
        id: "fromSum",
        value: amounts.fromSum,
        fixPrec: true,
      }
    });
  }, [currentRate]);  // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   amounts?.fixPrec && fixCalc();
  // }, [amounts]);

  const calculator = (direction, value) => {
    const { rate, precisions, operation } = currentRate;
    return calc({ direction, value, rate, precisions, operation });
  }

  const handleCalc = async (event, round = true) => {
    let { id, value, fixPrec } = event.target;

    if(typeof value === 'string') value = value.replace(',', '.');
    
    if (isNaN(value) || `${value}`.length > 14) return false;

    let amountResult = calculator(id, value);
    if (round) {
      amountResult = userRounding(amountResult);
    }
    setAmounts({ ...amountResult, fixPrec });
  };

  const fixCalc = (event) => {
    const { fromSum, toSum } = amounts;
    const { limits, precisions, fromMultiple, toMultiple } = currentRate;

    if (precisions.to === 0) {
      let newSum = calculator("toSum", toSum); // CALC DIRECT TO FROM
      if (newSum.fromSum < limits.min * 1) { // IF FROM SUM LOWER THEN LIMIT
        let newFromSum = calculator("fromSum", limits.min); // RECALC FROM LIMIT
        let newToSum = calculator("toSum", newFromSum.toSum);
        if (newToSum.fromSum < limits.min * 1) { // IF RECALC SUM IS LOWER THEN LIMIT
          sendToCalc("toSum", newFromSum.toSum * 1 + 1);
        } else {
          sendToCalc("toSum", newFromSum.toSum);
        }
      } else if (newSum.fromSum * 1 > limits.max * 1) {
        let newFromSum = calculator("fromSum", limits.max); // RECALC FROM LIMIT
        let newToSum = calculator("toSum", newFromSum.toSum);
        if (newToSum.fromSum > limits.max * 1) { // IF RECALC SUM IS HIGHER THEN LIMIT
          sendToCalc("toSum", newFromSum.toSum * 1 - 1);
        } else {
          sendToCalc("toSum", newFromSum.toSum);
        }
      } else {
        sendToCalc("toSum", toSum);
      }

    } else if (precisions.from === 0 && isEqual(calculator("fromSum", fromSum).toSum, toSum)) {
      // Fix from calc with precision
      sendToCalc("fromSum", fromSum, true);
    } else if (fromSum * 1 > limits.max * 1) {
      sendToCalc("fromSum", limits.max, true);
      return getText("exInputSumMaxAlert") + " " + limits.max + " " + currentRate.rateValues.fromCurrency;
    } else if (fromSum * 1 < limits.min * 1) {
      sendToCalc("fromSum", limits.min, true);
      return getText("exInputSumMinAlert") + " " + limits.min + " " + currentRate.rateValues.fromCurrency;
    }

    return true;
  }

  const userRounding = (data) => {
    let tempData = { ...data };
    const { fromSum, toSum } = data;
    const { fromMultiple, toMultiple } = currentRate;

    if (fromMultiple != 0) {
      tempData = {
        ...tempData,
        fromSum: getRoundedFromSum(fromSum, fromMultiple),
        fromSumOrig: fromSum,
      };
    } else {
      tempData = {
        ...tempData,
        fromSumOrig: null,
      };
    }

    if (toMultiple != 0) {
      tempData = {
        ...tempData,
        toSum: getRoundedDirSum(toSum, toMultiple),
        toSumOrig: toSum,
      };
    } else {
      tempData = {
        ...tempData,
        toSumOrig: null,
      };
    }
    return tempData;
  }

  const sendToCalc = (field, value, fix) => {
    handleCalc({
      target: {
        id: field,
        value: value,
        fixPrec: fix,
      }
    });
  }

  const onBlur = (e) => {
    let result = fixCalc(e);
    if (result !== true) {
      // setFromWarning({id: e.target.id, text: result});
      // setTimeout(() => setFromWarning({id: '', text: ''}), 5000);
    } else {
      let amountResult = userRounding(amounts);
      
      // if (directWasChanged.current && amountResult.toSumOrig && (amountResult.toSum != amountResult.toSumOrig)) {
      //   directWasChanged.current = false;
      //   setToMultipleTooltip(true);
      //   setTimeout(() => setToMultipleTooltip(false), 3000);
      // }

      // if (directWasChanged.current && amountResult.fromSumOrig && (amountResult.fromSum != amountResult.fromSumOrig)) {
      //   directWasChanged.current = false;
      //   setFromMultipleTooltip(true);
      //   setTimeout(() => setFromMultipleTooltip(false), 3000);
      // }

      setAmounts(amountResult)
    }
  }

  const handleSubmitOrder = () => {
    REST.repeatOrder(
      order.code,
      amounts.fromSum,
      currentRate.lastHash
    ).then((data) => {
      if (data.success) {
        window.open('/status/' + data.data.code, '_self');
      } else {
        if (data.data && data.data[0] && data.data[0].message) {
          window.showMessage(data.data[0].message);
        } else {
          window.showMessage("Ошибка сервера, попробуйте еще раз");
        }
      }
    })
  }

  const checkNotices = () => {
    let checked = false;
    [from.paymentSystem.notice, to.paymentSystem.notice].map(item => {
      if (item) checked = true;
    })
    return checked;
  }

  const { from = {}, to = {} } = currentRate;
  const parentRateValues = order.rateValues;
  let fromDiffLabel = '';
  let toDiffLabel = '';

  if (!loading) {
    const fromDiff = calcDelta(currentRate.rateValues.fromValue, parentRateValues.fromValue);
    const toDiff = calcDelta(currentRate.rateValues.toValue, parentRateValues.toValue);

    if (fromDiff != 0) {
      fromDiffLabel = <Typography variant="greenRateII">{fromDiff > 0 ? '+' : ''}{toFineFixed(fromDiff)} {currentRate.rateValues.fromCurrency}</Typography>
    }

    if (toDiff != 0) {
      toDiffLabel = <Typography variant="greenRateII"> {toDiff > 0 ? '+' : ''}{toFineFixed(toDiff)} {currentRate.rateValues.toCurrency}</Typography>
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => closeDialog()}
      maxWidth="lg"
    >
      <DialogTitle variant="h4" sx={{ fontWeight: 600, margin: '0 0 10px' }}>
        {getText('reorderH3')}
        <CloseIcon
          onClick={() => closeDialog()}
          style={{
            position: "absolute",
            right: 12,
            top: 10,
            cursor: 'pointer'
          }}
        />
        <Typography sx={{ marginTop: '7px' }}>{getText('reorderSubtitle')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ minHeight: '270px', position: 'relative' }}>
        {loading
          ? <span className="dot-spin centered"></span>
          : <>
            {updateRate ? <UpdateRateBox>
              <Typography variant="h5">{getText('rateRefreshH1')}</Typography>
              <Typography variant="caption" component="p">
                {getText('rateRefreshText')}
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{ paddingLeft: '40px', paddingRight: '40px', marginTop: '10px' }}
                onClick={() => getRepeatInfo(true)}
              >
                {getText('rateRefreshButton')}
              </Button>
            </UpdateRateBox> : <>
              <Grid container columns={11}>
                <Grid item sm={5} xs={11}>

                  <InputLabel htmlFor="fromSum">
                    <Typography variant="smallLabel">{getText('accOrdersThFrom')}</Typography>
                  </InputLabel>

                  <MyInput
                    value={amounts.fromSum}
                    id="fromSum"
                    type="text"
                    onBlur={(e) => onBlur(e)}
                    onChange={(e) => handleCalc(e, false)}
                    fullWidth
                    startAdornment={
                      <InputAdornment position="start">
                        <PSIcon name={from.paymentSystem.className} height={21} width={21} margin="0 2px 0 0" />
                      </InputAdornment>
                    }
                    endAdornment={<MyInputAdornment position="end">{currentRate.rateValues.fromCurrency}</MyInputAdornment>}
                  />

                  {Object.keys(from.clientDetails).length > 0
                    ? <Grid container spacing={1} sx={{ fontSize: '12px', margin: '15px 0', fontWeight: 500 }}>
                      {Object.keys(from.clientDetails).map((key) => (
                        <Grid key={key} item xs={6}>
                          {from.clientDetails[key].value}
                          <Box sx={{ color: 'primary.grey' }}>{from.clientDetails[key].label}</Box>
                        </Grid>
                      ))}
                    </Grid>
                    : <Box sx={{ color: 'primary.grey', fontWeight: 500 }}>{getText('formReqsNoNeed')}</Box>
                  }

                </Grid>
                <Grid item sm={1} xs={11} textAlign="center">
                  <ArrowRightBox><ArrowRight /></ArrowRightBox>
                </Grid>
                <Grid item sm={5} xs={11}>

                  <InputLabel htmlFor="toSum">
                    <Typography variant="smallLabel">{getText('accOrdersThTo')}</Typography>
                  </InputLabel>

                  <MyInput
                    value={amounts.toSum}
                    id="toSum"
                    type="text"
                    onBlur={(e) => onBlur(e)}
                    onChange={(e) => handleCalc(e, false)}
                    fullWidth
                    startAdornment={
                      <InputAdornment position="start">
                        <PSIcon name={to.paymentSystem.className} height={21} width={21} margin="0 2px 0 0" />
                      </InputAdornment>
                    }
                    endAdornment={<MyInputAdornment position="end">{currentRate.rateValues.toCurrency}</MyInputAdornment>}
                  />

                  {Object.keys(to.clientDetails).length > 0
                    ? <Grid container spacing={1} sx={{ fontSize: '12px', margin: '15px 0', fontWeight: 500 }}>
                      {Object.keys(to.clientDetails).map((key) => (
                        <Grid key={key} item xs={6}>
                          {to.clientDetails[key].value}
                          <Box sx={{ color: 'primary.grey' }}>{to.clientDetails[key].label}</Box>
                        </Grid>
                      ))}
                    </Grid>
                    : <Box sx={{ color: 'primary.grey', fontWeight: 500 }}>{getText('formReqsNoNeed')}</Box>
                  }

                </Grid>
              </Grid>

              {checkNotices() && <Notice
                type="warning"
                icon={<Warning />}
              >
                {[from.paymentSystem.notice, to.paymentSystem.notice].map((notice, key) => notice && <p key={key}>{notice}</p>)}
              </Notice>}

              <Grid container alignItems="center" sx={{ margin: '25px 0 0' }}>
                <Grid item sm={7} xs={12}>
                  <RateBox>
                    <Box className="item">
                      <PSIcon
                        name={from.paymentSystem.className}
                        height={28}
                        width={28}
                        margin="0 10px 0 0"
                      />
                      <Box sx={{ textAlign: 'left', position: 'relative', lineHeight: '18px' }}>
                        <Typography variant="boldCaption" component="div" fontWeight={700}>
                          {currentRate.rateValues.fromValue} {currentRate.rateValues.fromCurrency} {fromDiffLabel}
                        </Typography>
                        <Typography variant="greyCaption">{order.fromPaymentSystem}</Typography>
                      </Box>
                    </Box>
                    <Box className="arrow"><ArrowRight width="15" height="15" /></Box>
                    <Box className="item">
                      <PSIcon
                        name={to.paymentSystem.className}
                        height={28}
                        width={28}
                        margin="0 10px 0 0"
                      />
                      <Box sx={{ textAlign: 'left', position: 'relative', lineHeight: '18px' }}>
                        <Typography variant="boldCaption" component="div" fontWeight={700}>
                          {currentRate.rateValues.toValue} {currentRate.rateValues.toCurrency} {toDiffLabel}
                        </Typography>
                        <Typography variant="greyCaption">{order.toPaymentSystem}</Typography>
                      </Box>
                    </Box>
                  </RateBox>
                </Grid>
                <Grid item sm={5} xs={12} textAlign="right">
                  <Button
                    size="medium"
                    sx={{ padding: '15px 36px' }}
                    variant="contained"
                    onClick={handleSubmitOrder}
                    fullWidth={isXS}
                  >
                    {getText('reorderSubmitButton')}
                  </Button>
                </Grid>
              </Grid>
            </>}
          </>}
      </DialogContent>
    </Dialog>
  )
}

export default RepeatOrder